import React, { useMemo, useState } from 'react';
import { CircleF, GoogleMap, OverlayViewF } from '@react-google-maps/api';
import { MapPin, X } from 'lucide-react';
import { useGoogleMapContext } from '../../../context/GoogleMapContext';

const ALGERIA_CENTER = { lat: 28.0339, lng: 1.6596 };
const MAP_CONTAINER_STYLE = { height: '100%', width: '100%' };
const MAP_OPTIONS = { clickableIcons: false, fullscreenControl: false, mapTypeControl: false, streetViewControl: false };

const coordinatesFor = (doctor) => {
  if (doctor?.latitude === null || doctor?.latitude === '' || doctor?.longitude === null || doctor?.longitude === '') return null;
  const lat = Number(doctor?.latitude);
  const lng = Number(doctor?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const radiusZoom = (radiusKm) => {
  if (radiusKm <= 5) return 12;
  if (radiusKm <= 10) return 11;
  if (radiusKm <= 25) return 10;
  if (radiusKm <= 50) return 9;
  return 8;
};

export function DoctorSearchMap({ doctors = [], selectedIds = [], onToggle, onDoctorSelect, searchCenter, radiusKm, highlightedDoctorId, onDoctorHighlight }) {
  const { isLoaded } = useGoogleMapContext();
  const [hoveredDoctorId, setHoveredDoctorId] = useState(null);
  const [pinnedDoctorId, setPinnedDoctorId] = useState(null);
  const mappedDoctors = useMemo(() => doctors.filter((doctor) => coordinatesFor(doctor)), [doctors]);
  const centerCoordinates = coordinatesFor({ latitude: searchCenter?.latitude, longitude: searchCenter?.longitude });
  const center = centerCoordinates || coordinatesFor(mappedDoctors[0]) || ALGERIA_CENTER;
  const activeDoctorId = hoveredDoctorId || highlightedDoctorId || pinnedDoctorId;
  const activeDoctor = mappedDoctors.find((doctor) => String(doctor.id) === String(activeDoctorId));
  const legend = useMemo(() => Array.from(new Map(mappedDoctors.map((doctor) => [
    doctor.specialty || 'General medicine',
    doctor.specialtyColor || '#4AA3E0',
  ])).entries()), [mappedDoctors]);

  if (mappedDoctors.length === 0 && !centerCoordinates) {
    return <div className="collab-map collab-map--empty"><MapPin size={24} /><p>No mapped doctors match this search.</p></div>;
  }

  if (!isLoaded) {
    return <div className="collab-map collab-map--empty"><MapPin size={24} /><p>Loading the doctor map…</p></div>;
  }

  const clearHover = () => {
    setHoveredDoctorId(null);
    onDoctorHighlight?.(null);
  };

  const fitMappedDoctors = (map) => {
    if (centerCoordinates || mappedDoctors.length < 2 || !window.google?.maps?.LatLngBounds) return;
    const bounds = new window.google.maps.LatLngBounds();
    mappedDoctors.forEach((doctor) => bounds.extend(coordinatesFor(doctor)));
    map.fitBounds(bounds, 48);
  };

  return (
    <section className="collab-map" aria-label="Doctor locations">
      <GoogleMap mapContainerStyle={MAP_CONTAINER_STYLE} center={center} zoom={centerCoordinates && radiusKm ? radiusZoom(radiusKm) : 6} options={MAP_OPTIONS} onLoad={fitMappedDoctors}>
        {centerCoordinates && radiusKm ? (
          <CircleF
            center={centerCoordinates}
            radius={radiusKm * 1000}
            options={{ fillColor: '#4AA3E0', fillOpacity: 0.1, strokeColor: '#3286BE', strokeOpacity: 0.7, strokeWeight: 2 }}
          />
        ) : null}
        {mappedDoctors.map((doctor) => {
          const selected = selectedIds.includes(String(doctor.id));
          return (
            <OverlayViewF key={doctor.id} position={coordinatesFor(doctor)} mapPaneName="overlayMouseTarget">
              <button
                type="button"
                className={`collab-map__marker${selected ? ' is-selected' : ''}`}
                style={{ backgroundColor: doctor.specialtyColor || '#4AA3E0' }}
                aria-label={`View ${doctor.name} on map`}
                aria-pressed={selected}
                onMouseEnter={() => { setHoveredDoctorId(doctor.id); onDoctorHighlight?.(doctor.id); }}
                onMouseLeave={clearHover}
                onFocus={() => { setHoveredDoctorId(doctor.id); onDoctorHighlight?.(doctor.id); }}
                onBlur={clearHover}
                onClick={() => { setPinnedDoctorId(doctor.id); (onDoctorSelect || onToggle)(doctor); }}
              >
                <span className="sr-only">{doctor.specialty || 'General medicine'}</span>
              </button>
            </OverlayViewF>
          );
        })}
      </GoogleMap>

      {activeDoctor ? (
        <article className="collab-map__doctor-card" aria-live="polite">
          <button type="button" aria-label="Close doctor details" onClick={() => setPinnedDoctorId(null)}><X size={14} /></button>
          <p className="collab-eyebrow">{activeDoctor.distanceKm != null ? `${activeDoctor.distanceKm} km away` : 'Doctor location'}</p>
          <h3>{activeDoctor.name}</h3>
          <p><span style={{ backgroundColor: activeDoctor.specialtyColor || '#4AA3E0' }} />{activeDoctor.specialty || 'General medicine'}</p>
          <small>{activeDoctor.address || 'Practice address not provided'}</small>
        </article>
      ) : null}

      {legend.length > 0 ? (
        <div className="collab-map__legend" aria-label="Specialty colors">
          {legend.map(([specialty, color]) => <span key={specialty}><i style={{ backgroundColor: color }} />{specialty}</span>)}
        </div>
      ) : null}
    </section>
  );
}

export default DoctorSearchMap;
